import { AuthBaseClient } from '../base-client';
import type { SearchResponse } from '@vagusx/shared';
import type {
  DocumentResponse,
  UpdateDocumentRequest,
  DocumentSearchParams,
  CreateReferralRequest,
  CreateReferralResponse,
  ReferralExtraction,
} from './types';

/**
 * Client for document extraction API endpoints.
 */
export class DocumentClient extends AuthBaseClient {
  /**
   * Search document extractions.
   */
  async search(params?: DocumentSearchParams): Promise<SearchResponse<DocumentResponse>> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.reviewStatus) query.set('reviewStatus', params.reviewStatus);
    if (params?.patientId) query.set('patientId', params.patientId);
    if (params?.searchTerm) query.set('searchTerm', params.searchTerm);
    if (params?.urgency) query.set('urgency', params.urgency);
    if (params?.classification) query.set('classification', params.classification);
    if (params?.referringProviderName)
      query.set('referringProviderName', params.referringProviderName);
    if (params?.healthPlan) query.set('healthPlan', params.healthPlan);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    if (params?.sortOrder) query.set('sortOrder', params.sortOrder);
    const qs = query.toString();
    return this.get<SearchResponse<DocumentResponse>>(`/api/documents${qs ? `?${qs}` : ''}`);
  }

  /**
   * Get document extraction by ID.
   */
  async getById(id: string): Promise<DocumentResponse> {
    return this.get<DocumentResponse>(`/api/documents/${id}`);
  }

  /**
   * Update document extraction fields.
   */
  async update(id: string, data: UpdateDocumentRequest): Promise<DocumentResponse> {
    return this.patch<DocumentResponse>(`/api/documents/${id}`, data);
  }

  /**
   * Update review status.
   */
  async updateReviewStatus(id: string, reviewStatus: string): Promise<DocumentResponse> {
    return this.post<DocumentResponse>(`/api/documents/${id}/review`, {
      reviewStatus,
    });
  }

  /**
   * Match patient to document extraction.
   */
  async matchPatient(id: string, patientId: string): Promise<DocumentResponse> {
    return this.post<DocumentResponse>(`/api/documents/${id}/match-patient`, {
      patientId,
    });
  }

  /**
   * Unmatch patient from document extraction.
   */
  async unmatchPatient(id: string): Promise<DocumentResponse> {
    return this.delete<DocumentResponse>(`/api/documents/${id}/match-patient`);
  }

  /**
   * Create a new patient in the EHR from this document's extracted demographics and
   * link it to the document (ADR-089). Returns the document with `patientId` set.
   * Throws an operation-not-allowed validation error when the org's EHR has no
   * patient-write path (e.g. Office Ally), or when the document lacks a usable
   * name/DOB or an EHR location can't be derived.
   */
  async createPatientFromDocument(id: string): Promise<DocumentResponse> {
    return this.post<DocumentResponse>(`/api/documents/${id}/create-patient`, {});
  }

  /**
   * Match referring provider entity.
   */
  async matchReferringProvider(id: string, entityId: string): Promise<DocumentResponse> {
    return this.post<DocumentResponse>(`/api/documents/${id}/match-referring-provider`, {
      entityId,
    });
  }

  /**
   * Unmatch referring provider.
   */
  async unmatchReferringProvider(id: string): Promise<DocumentResponse> {
    return this.delete<DocumentResponse>(`/api/documents/${id}/match-referring-provider`);
  }

  /**
   * Match referring practice entity.
   */
  async matchReferringPractice(id: string, entityId: string): Promise<DocumentResponse> {
    return this.post<DocumentResponse>(`/api/documents/${id}/match-referring-practice`, {
      entityId,
    });
  }

  /**
   * Unmatch referring practice.
   */
  async unmatchReferringPractice(id: string): Promise<DocumentResponse> {
    return this.delete<DocumentResponse>(`/api/documents/${id}/match-referring-practice`);
  }

  /**
   * Match rendering provider.
   */
  async matchRenderingProvider(id: string, providerId: string): Promise<DocumentResponse> {
    return this.post<DocumentResponse>(`/api/documents/${id}/match-rendering-provider`, {
      providerId,
    });
  }

  /**
   * Unmatch rendering provider.
   */
  async unmatchRenderingProvider(id: string): Promise<DocumentResponse> {
    return this.delete<DocumentResponse>(`/api/documents/${id}/match-rendering-provider`);
  }

  /**
   * Match payer entity.
   */
  async matchPayer(id: string, entityId: string): Promise<DocumentResponse> {
    return this.post<DocumentResponse>(`/api/documents/${id}/match-payer`, {
      entityId,
    });
  }

  /**
   * Unmatch payer.
   */
  async unmatchPayer(id: string): Promise<DocumentResponse> {
    return this.delete<DocumentResponse>(`/api/documents/${id}/match-payer`);
  }

  /**
   * Create a referral document from structured JSON (no PDF / no extraction) and
   * attach a patient by mode (`existing` | `match` | `createInEhr`). The API/Postman
   * counterpart to the email-ingestion pipeline. See docs/referral-document-api.md.
   */
  async createReferral(body: CreateReferralRequest): Promise<CreateReferralResponse> {
    return this.post<CreateReferralResponse>('/api/documents', body);
  }

  /**
   * Extract referral fields from a PDF WITHOUT persisting anything — powers the
   * "From PDF" mode of the New-referral form. Runs the same vision extractor the
   * email-ingestion pipeline uses and returns form-shaped fields to prefill, which
   * the user reviews before {@link createReferral} actually creates the referral.
   * Multipart upload; the browser sets the boundary.
   */
  async extractReferralFromPdf(file: File): Promise<ReferralExtraction> {
    const formData = new FormData();
    formData.append('file', file);
    return this.uploadFormData<ReferralExtraction>('/api/documents/extract', formData);
  }

  /**
   * Attach (or replace) the source PDF on a document — uploaded to GCS and stored
   * as the document's `fileUrl`. Multipart upload; the browser sets the boundary.
   */
  async uploadPdf(id: string, file: File): Promise<DocumentResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.uploadFormData<DocumentResponse>(`/api/documents/${id}/pdf`, formData);
  }

  /**
   * Get the API streaming URL for a document extraction's PDF.
   * The PDF is streamed through the API — no GCS signed URLs exposed.
   */
  getPdfStreamUrl(id: string): string {
    return `${this.baseUrl}/api/documents/${id}/pdf`;
  }
}
